import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api.js';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { AlertTriangle, Phone } from 'lucide-react';
import { formatDateOrEmpty } from '@/lib/date-display.js';

// NEEDS A REAL NAME (2026-08-01).
//
// The phone intake creates a Contact even when the caller only gave a first
// name, because the lead is real and the pilot data has to land somewhere. It
// used to write Last_Name = 'Caller', which is indistinguishable from a person,
// so the placeholder healer in findOrCreateContact never touched those rows: 12
// Contacts, 7 carrying a real certificate, newest four days old.
//
// The writer is fixed. This is the other half: the rows already in CRM, and the
// one action that clears them.
//
// THESE ARE LEADS, NOT LITTER. Every row has a phone number and most have
// hours and a certificate, so the row leads with what David would need to make
// the call, not with the defect.
//
// The account rename is OPT-IN PER ROW and offered only where the account was
// built from the same partial name. 'Michael Byler & Kaleb Zimmerman' sits
// beside a contact named 'Michael Caller', and a couple's account is not a typo.

const fmtPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : String(p || '');
};

const fmtDate = (iso) => formatDateOrEmpty(iso);

// What we already know about this caller, as a sentence rather than a field
// list. An empty one renders nothing instead of a row of dashes.
function knownFacts(c) {
  return [
    c.certificate_type,
    c.total_flight_hours != null ? `${Number(c.total_flight_hours).toLocaleString('en-US')} hours` : null,
  ].filter(Boolean).join(' · ');
}

function ContactRow({ c, onSaved }) {
  const [first, setFirst] = useState(c.missing.includes('first') ? '' : (c.first_name || ''));
  const [last, setLast] = useState('');
  const [renameAccount, setRenameAccount] = useState(!!c.account_needs_name);
  const [saving, setSaving] = useState(false);

  const canSave = !saving && last.trim().length > 0;

  async function save() {
    setSaving(true);
    try {
      const res = await api.patch(`/api/admin/contact-names/${encodeURIComponent(c.id)}`, {
        first_name: first.trim() || null,
        last_name: last.trim(),
        rename_account: renameAccount && !!c.account_needs_name,
      });
      toast.success(`${[first.trim(), last.trim()].filter(Boolean).join(' ')} saved${res?.account_renamed ? ', account renamed' : ''}.`);
      for (const w of res?.warnings || []) toast.info(w);
      onSaved();
    } catch (e) {
      toast.error(e?.body?.error || e.message || 'Could not save the name');
    } finally {
      setSaving(false);
    }
  }

  const facts = knownFacts(c);
  return (
    <div className="rounded-card border border-border bg-surface p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-text">{c.full_name}</span>
        {c.phone ? (
          <a href={`tel:${c.phone}`} className="flex items-center gap-1 text-[12.5px] text-accent hover:underline">
            <Phone size={12} /> {fmtPhone(c.phone)}
          </a>
        ) : (
          <span className="text-[12px] text-text-dim">no phone on file</span>
        )}
        {facts ? <span className="text-[12px] text-text-muted">{facts}</span> : null}
        <span className="ml-auto text-[11.5px] text-text-dim">{fmtDate(c.created_time)}</span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <Input
          value={first}
          onChange={(e) => setFirst(e.target.value)}
          placeholder="First name"
          className="h-9 w-40"
          aria-label={`First name for ${c.full_name}`}
        />
        <Input
          value={last}
          onChange={(e) => setLast(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && canSave) save(); }}
          placeholder="Last name"
          className="h-9 w-48"
          aria-label={`Last name for ${c.full_name}`}
        />
        <Button onClick={save} disabled={!canSave} className="h-9">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
        <span className="text-text-dim">Account</span>
        <span className="text-text-muted">{c.account_name || 'none'}</span>
        {c.account_needs_name ? (
          <label className="flex items-center gap-1.5 text-text">
            <input
              type="checkbox"
              checked={renameAccount}
              onChange={(e) => setRenameAccount(e.target.checked)}
              className="h-3.5 w-3.5 accent-accent"
            />
            rename it to match
          </label>
        ) : (
          <span className="text-text-dim">looks like a real name, leaving it alone</span>
        )}
      </div>
    </div>
  );
}

// At "both" the server names the people who need a name but cannot be repaired here
// (people-create-door C7): no CRM contact yet, or a contact the CRM list does not hold.
const NOT_LISTED_WORDS = { no_crm_contact: 'no CRM contact', not_in_crm_list: 'contact not in the CRM list' };
function NotListed({ people }) {
  const groups = {};
  for (const p of people) (groups[p.reason] = groups[p.reason] || []).push(p.person_id);
  return (
    <p className="max-w-2xl text-[12.5px] text-text-dim">
      {people.length} more {people.length === 1 ? 'person needs' : 'people need'} a real name but cannot be repaired here:{' '}
      {Object.entries(groups).map(([reason, ids]) => `${NOT_LISTED_WORDS[reason] || reason} (person ${ids.join(', ')})`).join('; ')}.
    </p>
  );
}

export default function ContactNamesAdmin() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [notListed, setNotListed] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const d = await api.get(`/api/admin/contact-names?_t=${Date.now()}`);
      setRows(Array.isArray(d?.contacts) ? d.contacts : []);
      setNotListed(Array.isArray(d?.not_listed) ? d.not_listed : []);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load the list');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const withCerts = useMemo(
    () => (rows || []).filter((c) => c.certificate_type || c.total_flight_hours != null).length,
    [rows],
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-text">Needs a real name</h1>
        <p className="mt-1 max-w-2xl text-[13px] text-text-muted">
          Callers the phone intake had to record before it heard a full name. They are real
          leads: give one its name and the contact stops reading as a placeholder everywhere
          it appears.
        </p>
      </div>

      {!error && notListed.length ? <NotListed people={notListed} /> : null}

      {error ? (
        <p className="flex items-center gap-2 text-sm text-danger">
          <AlertTriangle size={14} /> {error}
        </p>
      ) : rows === null ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-card border border-dashed border-border p-4 text-sm text-text-muted">
          Nothing waiting. Every contact has a real name.
        </p>
      ) : (
        <>
          <p className="text-[12.5px] text-text-dim">
            {rows.length} contact{rows.length === 1 ? '' : 's'}
            {withCerts ? `, ${withCerts} already carrying a certificate or hours` : ''}
          </p>
          <div className="space-y-2">
            {rows.map((c) => (
              <ContactRow key={c.id} c={c} onSaved={refresh} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
